/**
 * Immediate visual feedback for Add to Cart buttons marked with
 * [data-loader-after-click]. Shows a spinner on click so the user gets
 * instant feedback while third-party validators (ShopPad Infinite
 * Options + Uploadery) do their async work.
 *
 * Also pre-validates required options on a real user click BEFORE showing the
 * spinner: if a visible required field is empty we stop the event (so ShopPad's
 * unstyled native alert() never fires), skip the spinner, and show a DS-styled
 * modal listing what's missing plus per-field inline errors. See the
 * "Required-option pre-validation" block below.
 *
 * Do not set the HTML `disabled` attribute — that stops the button being a
 * valid submitter and cancels the native submission ShopPad falls back to
 * (ENG-824). Use aria-disabled + pointer-events instead, plus a capture-phase
 * swallow of repeat clicks while the form is in flight (stale IO group IDs).
 *
 * Never mutate button / `.button-text` DOM during the click event.
 * ShopPad IO attaches hidden properties (e.g. `_io_order_group`) in its
 * click handler; replacing `innerHTML` mid-event detaches the click
 * target and drops those fields from the parent line item (ENG-824).
 * Spinner is deferred with setTimeout(0) and only hides `.button-text`
 * (when the span exists) then appends a sibling — existing nodes stay in
 * place. On ShopPad-validated buttons the label is a bare text node with no
 * `.button-text` span; we leave it and just add the spinner beside it.
 *
 * The in-flight guard must swallow ONLY a real user's repeat click. ShopPad
 * Infinite Options completes an add by programmatically RE-FIRING the click —
 * `$(button).trigger("click", {skip_processing:true})` — a synthetic event with
 * `isTrusted === false`. Swallowing that re-fire stops the form ever submitting,
 * so nothing lands in the cart (the ENG-991 regression). The guard is therefore
 * gated on `event.isTrusted`: genuine double-clicks (trusted) are swallowed;
 * ShopPad's re-fire (untrusted) passes straight through.
 *
 * Restore the original button text on: the theme's own AJAX add-to-cart
 * settling, ShopPad inline error (via a scoped MutationObserver on
 * #infiniteoptions-container), HTML5 form validation cancellation, page
 * bfcache restore, or after SAFETY_TIMEOUT_MS as a last-resort fallback.
 */
(() => {
  'use strict';

  // Inline SVG spinner — matches the UK store's `.submit-spinner` (Phosphor
  // spinner glyph, see stores/uk/src/assets/bold-accordian.js). Inline SVG (not
  // the `ph` icon font) so it always renders, animated with Tailwind's
  // `animate-spin`. `inline-block` is required — CSS transforms (and therefore
  // the rotation) don't apply to `display: inline` elements.
  const SPINNER_HTML = `<svg class="animate-spin inline-block align-middle" width="18" height="18" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M232,128a104,104,0,0,1-208,0c0-41,23.81-78.36,60.66-95.27a8,8,0,0,1,6.68,14.54C60.15,61.59,40,93.27,40,128a88,88,0,0,0,176,0c0-34.73-20.15-66.41-51.34-80.73a8,8,0,0,1,6.68-14.54C208.19,49.64,232,87,232,128Z"></path></svg>`;

  const SAFETY_TIMEOUT_MS = 8_000;
  const ACTIVE_FLAG = 'atcFeedbackActive';
  const SPINNER_SELECTOR = '[data-atc-spinner]';

  const IO_ERROR_SELECTOR = '.spb-error, .shoppad--io--error, .spb-fielderror';

  const safetyTimers = new WeakMap();
  // Buttons whose submit was cancelled by validation before their deferred
  // showSpinner ran - skip activating the spinner for these (ENG-824).
  const cancelledButtons = new WeakSet();
  // Forms with an ATC click in flight — set synchronously so a same-tick
  // double-click is swallowed before ShopPad's handler runs.
  const inFlightForms = new WeakSet();

  const clearFormInFlight = (form) => {
    if (!form) return;
    inFlightForms.delete(form);
    form.querySelectorAll('[data-loader-after-click]').forEach((button) => {
      button.removeAttribute('aria-disabled');
      button.style.pointerEvents = '';
    });
  };

  const showSpinner = (button) => {
    if (!button || button.dataset[ACTIVE_FLAG] === '1') return;
    if (cancelledButtons.has(button)) {
      cancelledButtons.delete(button);
      clearFormInFlight(button.form);
      return;
    }

    // Never move, wrap, or replace the button's DOM. ShopPad IO attaches
    // `_io_order_group` to the button in its own click handler and relies on it
    // staying intact (ENG-824). So instead of touching the label node, we paint
    // the button's text transparent — hiding both a `.button-text` span and a
    // bare ShopPad text node *in place* — and overlay a spinner centred on the
    // whole button, so it visually REPLACES the label instead of sitting beside
    // it. Nothing in the DOM moves.
    button.dataset[ACTIVE_FLAG] = '1';
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
    button.style.pointerEvents = 'none';

    // Hide the label in place. The theme colours the button text with
    // `-webkit-text-fill-color`, which beats `color`, so we force BOTH to
    // transparent (with !important, to win over the theme rule). Unlike
    // font-size:0 this keeps the button's height. Capture the rendered text
    // colour first so the spinner keeps it, and record whether we had to force a
    // positioned containing block (the spinner is absolutely positioned, so the
    // button needs one — only add it if the button is currently static).
    const cs = getComputedStyle(button);
    const restColor = cs.webkitTextFillColor || cs.color;
    button.dataset.atcPrevPos = button.style.position;
    if (cs.position === 'static') {
      button.style.position = 'relative';
    }
    button.style.setProperty('color', 'transparent', 'important');
    button.style.setProperty('-webkit-text-fill-color', 'transparent', 'important');

    // A transparent -webkit-text-fill-color still shows through the browser's
    // text SELECTION highlight, so rapid repeat-clicks (which select the label)
    // progressively "reveal" the hidden text. Make the button unselectable while
    // spinning, and drop any selection that formed on the opening click.
    button.style.userSelect = 'none';
    button.style.webkitUserSelect = 'none';
    try {
      const sel = window.getSelection();
      if (sel && sel.removeAllRanges) sel.removeAllRanges();
    } catch (err) {
      /* clearing the selection is best-effort */
    }

    let spinner = button.querySelector(SPINNER_SELECTOR);
    if (!spinner) {
      spinner = document.createElement('span');
      spinner.dataset.atcSpinner = '1';
      spinner.setAttribute('aria-hidden', 'true');
      // Centre the spinner over the whole button (absolute + flex) and give it
      // the captured colour so it stays visible against the transparent label.
      spinner.style.cssText =
        'position:absolute;inset:0;display:flex;align-items:center;' +
        'justify-content:center;color:' + restColor + ';';
      spinner.innerHTML = SPINNER_HTML;
      button.appendChild(spinner);
    }

    safetyTimers.set(
      button,
      window.setTimeout(() => restoreButton(button), SAFETY_TIMEOUT_MS)
    );
  };

  const restoreButton = (button) => {
    // Clear in-flight even when the spinner never activated (e.g. invalid).
    clearFormInFlight(button?.form);

    // Always clear any pending safety timer BEFORE the ACTIVE_FLAG early return
    // below, so a settle signal that arrives while ACTIVE_FLAG is somehow unset
    // can't leave a stale timer to fire during a later submission of the same
    // button and prematurely release its double-submit guard (ENG-991).
    if (button) {
      const timer = safetyTimers.get(button);
      if (timer) {
        window.clearTimeout(timer);
        safetyTimers.delete(button);
      }
    }

    if (!button || button.dataset[ACTIVE_FLAG] !== '1') return;

    button.querySelector(SPINNER_SELECTOR)?.remove();

    // Un-hide the label: drop the forced transparent colours and restore the
    // button's original inline position (empty reverts to the stylesheet value).
    button.style.removeProperty('color');
    button.style.removeProperty('-webkit-text-fill-color');
    button.style.userSelect = '';
    button.style.webkitUserSelect = '';
    button.style.position = button.dataset.atcPrevPos || '';
    delete button.dataset.atcPrevPos;
    button.removeAttribute('aria-busy');

    delete button.dataset[ACTIVE_FLAG];
  };

  const restoreAll = () => {
    document
      .querySelectorAll('[data-loader-after-click]')
      .forEach(restoreButton);
  };

  /* ────────────────────────────────────────────────────────────────────────
   * Required-option pre-validation (US Infinite Options).
   *
   * ShopPad validates required options in its own overlay click handler and, on
   * a miss, throws a native `alert("Please select an option…")` and stops — an
   * unstyled dialog. And because we show the loader on click, that left the
   * spinner stuck until the 8s safety timer (the loader spinning on a failed
   * validation, then "un-spinning" on its own).
   *
   * Instead we pre-check the required fields in our own capture-phase handler
   * (which runs before ShopPad's overlay handler) and, when something's missing,
   * stop the event so ShopPad's alert never fires, skip the spinner entirely,
   * and show a DS-styled modal plus per-field inline errors. We only intercept
   * when WE find an empty *visible* required field; any other ShopPad validation
   * still falls through to ShopPad. Uses ShopPad's own `[required]` markers so it
   * doesn't drift if the option set changes. DS tokens mirror consent-banner-styles.
   * ──────────────────────────────────────────────────────────────────────── */
  const FIELD_ERROR_CLASS = 'lf-atc-field-error';
  const INVALID_FIELD_CLASS = 'lf-atc-invalid';
  const MODAL_ID = 'lf-atc-validate-modal';
  const STYLE_ID = 'lf-atc-validate-styles';
  let modalFields = [];
  let modalKeyHandler = null;

  const injectValidationStyles = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      `#${MODAL_ID}{position:fixed;inset:0;z-index:2147483646;display:flex;align-items:center;justify-content:center;padding:20px;background:var(--lf-consent-overlay,rgba(31,28,29,.6));-webkit-backdrop-filter:blur(1px);backdrop-filter:blur(1px);animation:lf-atc-fade 200ms ease-out;}`,
      `#${MODAL_ID}[hidden]{display:none;}`,
      `#${MODAL_ID} .lf-atc-card{position:relative;box-sizing:border-box;width:100%;max-width:440px;max-height:calc(100vh - 40px);overflow-y:auto;padding:28px;background:var(--lf-white,#fff);color:var(--lf-grey-900,#1f1c1d);border:1px solid var(--lf-grey-150,#e5e0e2);border-radius:16px;box-shadow:0 20px 25px -5px rgba(0,0,0,.1),0 10px 10px 0 rgba(0,0,0,.04);font-family:"Messina Sans",sans-serif;animation:lf-atc-pop 240ms cubic-bezier(.16,1,.3,1);}`,
      `#${MODAL_ID} .lf-atc-title{font-family:"Fraunces LF","Fraunces",serif;font-weight:400;font-size:1.5rem;line-height:1.25;color:var(--lf-grey-900,#1f1c1d);margin:0 0 8px;padding-right:24px;}`,
      `#${MODAL_ID} .lf-atc-sub{font-size:.9375rem;line-height:1.5;color:var(--lf-grey-700,#595355);margin:0 0 16px;}`,
      `#${MODAL_ID} .lf-atc-list{list-style:none;margin:0 0 22px;padding:0;}`,
      `#${MODAL_ID} .lf-atc-list li{display:flex;align-items:center;gap:10px;padding:6px 0;color:var(--lf-grey-900,#1f1c1d);font-size:.9375rem;}`,
      `#${MODAL_ID} .lf-atc-list li::before{content:"";flex:0 0 auto;width:8px;height:8px;border-radius:50%;background:#b91c1c;}`,
      `#${MODAL_ID} .lf-atc-cta{display:block;width:100%;box-sizing:border-box;margin:0;padding:15px 20px;font-family:"Messina Sans",sans-serif;font-size:1rem;font-weight:400;line-height:1.1;text-align:center;border:1px solid transparent;border-radius:8px;cursor:pointer;background:var(--lf-grey-900,#1f1c1d);color:var(--lf-white,#fff);transition:background-color 150ms ease;}`,
      `#${MODAL_ID} .lf-atc-cta:hover,#${MODAL_ID} .lf-atc-cta:focus{background:#000;}`,
      `#${MODAL_ID} .lf-atc-close{position:absolute;top:14px;right:16px;width:28px;height:28px;padding:0;display:inline-flex;align-items:center;justify-content:center;border:0;background:transparent;color:var(--lf-grey-700,#595355);cursor:pointer;border-radius:6px;}`,
      `#${MODAL_ID} .lf-atc-close svg{width:18px;height:18px;display:block;}`,
      `#${MODAL_ID} .lf-atc-close:hover{background:var(--lf-grey-100,#f2f0f1);color:var(--lf-grey-900,#1f1c1d);}`,
      `.${INVALID_FIELD_CLASS}{border-color:#b91c1c !important;box-shadow:0 0 0 1px #b91c1c !important;}`,
      `.${FIELD_ERROR_CLASS}{display:flex;align-items:center;gap:6px;margin-top:6px;color:#b91c1c;font-family:"Messina Sans",sans-serif;font-size:.8125rem;font-weight:500;line-height:1.3;}`,
      `.${FIELD_ERROR_CLASS}::before{content:"!";display:inline-flex;align-items:center;justify-content:center;flex:0 0 auto;width:15px;height:15px;border-radius:50%;background:#b91c1c;color:#fff;font-size:11px;font-weight:700;}`,
      `@keyframes lf-atc-fade{from{opacity:0;}to{opacity:1;}}`,
      `@keyframes lf-atc-pop{from{opacity:0;transform:translateY(8px) scale(.98);}to{opacity:1;transform:none;}}`,
      `@media (prefers-reduced-motion:reduce){#${MODAL_ID},#${MODAL_ID} .lf-atc-card{animation:none !important;}}`,
    ].join('');
    (document.head || document.documentElement).appendChild(style);
  };

  // A field only counts if it is actually on screen — ShopPad hides conditional
  // options (e.g. the Framing select that doesn't match the chosen Size) with
  // display:none, which zeroes offsetParent.
  const isFieldVisible = (el) => !!el && el.offsetParent !== null;

  const fieldLabel = (el) => {
    const group =
      el.closest('#infiniteoptions-container > div') ||
      el.closest('.spb-label-tooltip')?.parentElement ||
      el.parentElement;
    const lbl = group?.querySelector('.spb-label-tooltip label, label');
    let text = (lbl?.textContent || '').replace(/\s+/g, ' ').trim();
    if (!text) {
      const name = el.getAttribute('name') || '';
      const m = name.match(/properties\[(.+?)\]/i);
      text = m
        ? m[1]
        : (el.id || '').replace(/-\d+-\d+$/, '').replace(/[-_]+/g, ' ').trim();
    }
    return text || 'this option';
  };

  const isGroupControl = (el) => el.type === 'radio' || el.type === 'checkbox';

  // All controls in a radio/checkbox group, resolved by NAME through the form's
  // elements collection (namedItem) — never a CSS selector, so a merchant-defined
  // option name containing a quote or backslash can't throw. Falls back to the
  // single control for a non-grouped or nameless field.
  const groupMembers = (el) => {
    const form = el.form;
    if (!form || !el.name) return [el];
    const named = form.elements.namedItem(el.name);
    if (!named) return [el];
    if (named instanceof Element) return [named];
    return Array.prototype.slice.call(named); // RadioNodeList
  };

  const missingRequired = (form) => {
    if (!form?.querySelectorAll) return [];
    const out = [];
    const seenGroups = new Set();
    form.querySelectorAll('[required]').forEach((el) => {
      if (el.disabled || !isFieldVisible(el)) return;
      if (isGroupControl(el)) {
        if (seenGroups.has(el.name)) return;
        seenGroups.add(el.name);
        if (!groupMembers(el).some((r) => r.checked)) out.push(el);
      } else if (!String(el.value || '').trim()) {
        out.push(el);
      }
    });
    return out;
  };

  const errorTextFor = (el) => {
    if (el.tagName === 'SELECT') return 'Please choose an option';
    if (el.type === 'file') return 'Please add your photo';
    return 'Please fill this in';
  };

  const clearFieldError = (el) => {
    el.classList.remove(INVALID_FIELD_CLASS);
    const anchor = el.closest('.spb-select') || el;
    const next = anchor.nextElementSibling;
    if (next?.classList?.contains(FIELD_ERROR_CLASS)) next.remove();
  };

  const showFieldError = (el) => {
    el.classList.add(INVALID_FIELD_CLASS);
    const anchor = el.closest('.spb-select') || el;
    if (anchor.nextElementSibling?.classList?.contains(FIELD_ERROR_CLASS)) return;
    const msg = document.createElement('span');
    msg.className = FIELD_ERROR_CLASS;
    msg.textContent = errorTextFor(el);
    anchor.insertAdjacentElement('afterend', msg);
    // Clear on fill. For a radio/checkbox group the customer may satisfy the
    // requirement by picking a DIFFERENT control, so listen on every member and
    // clear once any of them is checked.
    const grouped = isGroupControl(el);
    const members = grouped ? groupMembers(el) : [el];
    const clear = () => {
      const satisfied = grouped
        ? members.some((n) => n.checked)
        : String(el.value || '').trim() !== '';
      if (satisfied) clearFieldError(el);
    };
    members.forEach((n) => {
      n.addEventListener('change', clear);
      n.addEventListener('input', clear);
    });
  };

  const hideValidationModal = (focusEl) => {
    const modal = document.getElementById(MODAL_ID);
    if (modal) modal.hidden = true;
    if (modalKeyHandler) {
      document.removeEventListener('keydown', modalKeyHandler);
      modalKeyHandler = null;
    }
    if (focusEl && isFieldVisible(focusEl)) {
      try {
        focusEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        focusEl.focus({ preventScroll: true });
      } catch (err) {
        /* focus/scroll is best-effort */
      }
    }
  };

  const showValidationModal = (fields) => {
    injectValidationStyles();
    let modal = document.getElementById(MODAL_ID);
    if (!modal) {
      modal = document.createElement('div');
      modal.id = MODAL_ID;
      modal.hidden = true;
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', MODAL_ID + '-title');
      modal.innerHTML =
        '<div class="lf-atc-card" role="document">' +
        '<button type="button" class="lf-atc-close" aria-label="Close">' +
        '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>' +
        '</button>' +
        '<h2 class="lf-atc-title" id="' +
        MODAL_ID +
        '-title">Before you add to cart</h2>' +
        '<p class="lf-atc-sub">Please choose the following to continue:</p>' +
        '<ul class="lf-atc-list"></ul>' +
        '<button type="button" class="lf-atc-cta">Got it</button>' +
        '</div>';
      document.body.appendChild(modal);
      const dismiss = () => hideValidationModal(modalFields[0]);
      modal.addEventListener('click', (e) => {
        if (e.target === modal) dismiss();
      });
      modal.querySelector('.lf-atc-close').addEventListener('click', dismiss);
      modal.querySelector('.lf-atc-cta').addEventListener('click', dismiss);
    }
    const list = modal.querySelector('.lf-atc-list');
    const seen = new Set();
    list.innerHTML = '';
    fields.forEach((el) => {
      const label = fieldLabel(el);
      const key = label.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      const li = document.createElement('li');
      li.textContent = label;
      list.appendChild(li);
    });
    modalFields = fields;
    modal.hidden = false;
    modalKeyHandler = (e) => {
      if (e.key === 'Escape') hideValidationModal(modalFields[0]);
    };
    document.addEventListener('keydown', modalKeyHandler);
    try {
      modal.querySelector('.lf-atc-cta').focus({ preventScroll: true });
    } catch (err) {
      /* best-effort focus */
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target?.closest?.('[data-loader-after-click]');
      if (!button) return;
      if (button.disabled) return;

      const form = button.form;

      // Pre-validate required options on a REAL user click, before any spinner
      // shows or ShopPad's native alert() fires. Only intercept when WE detect an
      // empty visible required field; otherwise defer to ShopPad. Wrapped so a
      // validation-UI bug can never block add-to-cart.
      if (event.isTrusted) {
        try {
          const missing = missingRequired(form);
          if (missing.length) {
            event.preventDefault();
            event.stopImmediatePropagation();
            missing.forEach(showFieldError);
            showValidationModal(missing);
            return;
          }
        } catch (err) {
          /* fall through to the normal add flow */
        }
      }
      // Only swallow a REAL user's repeat click (i.e. a genuine double-click).
      // ShopPad Infinite Options completes its add by programmatically RE-FIRING
      // the click — `$(button).trigger("click", {skip_processing:true})` — which is
      // a synthetic event with `isTrusted === false`. We must let that through, or
      // the form never submits and nothing is added to the cart (this was the
      // ENG-991 regression). Real user clicks are `isTrusted === true`.
      if (
        event.isTrusted &&
        (button.getAttribute('aria-disabled') === 'true' ||
          (form && inFlightForms.has(form)))
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (form) inFlightForms.add(form);

      // After ShopPad's click handlers finish attaching _io_order_group.
      window.setTimeout(() => showSpinner(button), 0);
    },
    true
  );

  // Empty _io_order_group seed must not submit, but must stay in the DOM for later AJAX ATC (ENG-824).
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form?.querySelectorAll) return;
      form.querySelectorAll('[data-io-order-group-seed]').forEach((input) => {
        if (input.value) return;
        input.disabled = true;
        window.setTimeout(() => {
          input.disabled = false;
        }, 0);
      });
    },
    true
  );

  // HTML5 validation cancels the submit - reset so user can fix and retry.
  // `invalid` doesn't bubble, so capture-phase.
  document.addEventListener(
    'invalid',
    (event) => {
      const form = event.target?.form;
      if (!form) return;
      form.querySelectorAll('[data-loader-after-click]').forEach((button) => {
        cancelledButtons.add(button);
        restoreButton(button);
      });
    },
    true
  );

  // Belt-and-braces: ShopPad Infinite Options can fail validation or AJAX
  // *after* a form submit has fired (e.g. Uploadery file POST failure). If
  // its inline error node appears in the IO container, restore any spinning
  // ATC button so the user isn't stuck waiting for the safety timeout.
  const watchIoErrors = () => {
    const ioContainer = document.querySelector('#infiniteoptions-container');
    if (!ioContainer) return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          if (node.matches?.(IO_ERROR_SELECTOR) || node.querySelector?.(IO_ERROR_SELECTOR)) {
            restoreAll();
            return;
          }
        }
      }
    });

    observer.observe(ioContainer, { childList: true, subtree: true });
  };

  // Forms marked [data-ajax-add-to-cart] are submitted by the theme's own
  // handler, which disables submit buttons for the POST and re-enables them on
  // both success and handled error without navigating. Treat that re-enable as
  // the settle signal, otherwise the in-flight guard holds the button until
  // SAFETY_TIMEOUT_MS and the customer can't add another unit or retry.
  const watchThemeRelease = () => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (!mutation.target.disabled) restoreButton(mutation.target);
      }
    });

    document
      .querySelectorAll('[data-loader-after-click]')
      .forEach((button) =>
        observer.observe(button, { attributeFilter: ['disabled'] })
      );
  };

  const init = () => {
    watchIoErrors();
    watchThemeRelease();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Back/forward nav from bfcache shouldn't show a stuck spinner.
  window.addEventListener('pageshow', restoreAll);
})();
